import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DatePicker,
  DatePickerSlotProps,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Locale, enUS, ja } from "date-fns/locale";
import { DATE_TIME_FORMAT_JP, MIN_DATE_REPORT_SUMMARY } from "@app/constants";
import { getMaxDate, isMobile } from "@app/helpers";
import { getUser } from "@app/api";

interface LocalesMap {
  [key: string]: Locale;
}

const locales: LocalesMap = {
  en: enUS,
  ja,
};

export interface DatePickerMachineReportHistProps {
  label?: string;
  value: Date;
  onChange: (newValue: Date | null) => void;
  onAccept: (newValue: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  open?: boolean;
  onClose?: () => void;
  views?: ("year" | "month" | "day")[];
  slotProps?: DatePickerSlotProps<Date, false>;
  openTo?: "year" | "month" | "day";
}

export const DatePickerMachineReportHist: FC<
  DatePickerMachineReportHistProps
> = ({
  label,
  value,
  onChange,
  onAccept,
  minDate,
  maxDate,
  open,
  onClose,
  views = ["year", "month", "day"],
  slotProps,
  openTo,
}: DatePickerMachineReportHistProps) => {
  const { i18n } = useTranslation();
  const locale = locales[i18n?.language];
  const [userTimeFormat, setUserTimeFormat] = useState<string>("yyyy/MM/dd");
  const [userTimeFormat1, setUserTimeFormat1] = useState<string>("yyyy/MM/dd");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        const timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";
        setUserTimeFormat(timeFormat);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await getUser();
        let timeFormat = response?.data?.data?.timeFormat || "yyyy/MM/dd";

        const formatsToModify = ["yyyy/MM/dd", "MM/dd/yyyy", "dd/MM/yyyy"];
        if (formatsToModify.includes(timeFormat)) {
          timeFormat = timeFormat.replace(/^dd\//, "").replace(/\/dd/, "");
        }

        setUserTimeFormat1(timeFormat);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DatePicker
        label={label}
        value={value}
        onChange={isMobile() ? undefined : onChange}
        onAccept={isMobile() ? onAccept : undefined}
        views={views}
        open={open}
        onClose={onClose}
        disableFuture
        minDate={new Date(MIN_DATE_REPORT_SUMMARY)}
        maxDate={maxDate ?? getMaxDate()}
        format={userTimeFormat}
        slotProps={{
          ...slotProps,
          textField: { style: { display: "none" } },
          toolbar: {
            toolbarFormat:
              locale === locales.ja ? DATE_TIME_FORMAT_JP : undefined,
          },
          calendarHeader: {
            format: userTimeFormat1,
          },

          dialog: {
            sx: {
              "& .MuiPaper-root": {
                minWidth: "unset",
                width: "unset",
              },
              "& .MuiPickersCalendarHeader-label": {
                fontWeight: 700,
              },
              "& .MuiTypography-overline": {
                color: "#fff",
              },
              "& .MuiPickersLayout-contentWrapper": {
                borderTop: "1px solid #e4e4e4",
              },
              "& .MuiDialogActions-root .MuiButton-root": {
                textTransform: "none",
              },
            },
          },
        }}
        openTo={openTo}
      />
    </LocalizationProvider>
  );
};
